import { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faLink,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { BaseRecipient } from "app/containers/WeeklyCheckInMonitor/types";
import { Button as ShadcnButton } from "app/shadcnComponents/ui/button";
import AvatarCircle from "app/components/AvatarCircle";
import { cn } from "app/lib/utils";
import { formatRelativeTime } from "utils/helperFunctions";
import { ACTION_CONFIG, ActionType, STATUS_DOT } from "./types";

export default function RecipientRow<T extends BaseRecipient>({
  recipient,
  onAction,
  isSaving,
  actionType,
  renderSubtitle,
}: Readonly<{
  recipient: T;
  onAction: (userAccountId: number) => void;
  isSaving: boolean;
  actionType: ActionType;
  renderSubtitle: (recipient: T) => ReactNode;
}>) {
  const fullName = `${recipient.firstName} ${recipient.lastName}`;
  const dotColor = STATUS_DOT[recipient.status];
  const action = ACTION_CONFIG[actionType];
  const isDimmed =
    recipient.status === "excluded" ||
    recipient.status === "below_threshold" ||
    recipient.status === "not_included";

  const getBadgeText = () => {
    if (recipient.status === "included_override" && !recipient.percentInvited) {
      return "Won't send";
    }
    if (recipient.status === "below_threshold") {
      return "Below threshold";
    }
    return null;
  };

  const getAttributionText = () => {
    if (!recipient.overrideCreatedByName || !recipient.overrideCreatedAt) {
      return null;
    }
    const timeAgo = formatRelativeTime(recipient.overrideCreatedAt);
    if (recipient.status === "excluded") {
      return `Excluded by ${recipient.overrideCreatedByName} · ${timeAgo}`;
    }
    if (
      recipient.status === "included_override" ||
      recipient.status === "qualified_by_rules"
    ) {
      return `Added by ${recipient.overrideCreatedByName} · ${timeAgo}`;
    }
    return null;
  };

  const badgeText = getBadgeText();
  const attributionText = getAttributionText();

  return (
    <div
      className={cn(
        "flex gap-3 py-2.5 px-6 hover:bg-charcoal-02 transition-colors",
        isDimmed && "opacity-70"
      )}
    >
      <AvatarCircle
        name={fullName}
        userAccountId={recipient.userAccountId}
        size="small"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", dotColor)}
          />
          <span className="text-sm font-medium text-charcoal-150">
            {fullName}
          </span>
          {badgeText && (
            <span className="text-[11px] font-medium rounded-full px-[8px] py-[2px] leading-none inline-flex items-center gap-1 bg-amber-50 text-amber-700 border-[1px] border-amber-200">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="text-[8px]"
              />
              {badgeText}
            </span>
          )}
        </div>
        <div className="text-xs text-charcoal-80 mt-0.5">
          {renderSubtitle(recipient)}
        </div>
        {recipient.reason && (
          <div className="text-xs leading-[16px] mt-1 text-charcoal-80">
            {recipient.reason}
          </div>
        )}
        {recipient.alsoQualifies && (
          <div className="flex items-center gap-1 text-[11px] text-sapphire-100 mt-1">
            <FontAwesomeIcon icon={faLink} className="text-[9px]" />
            Also qualifies under current rules
          </div>
        )}
        {attributionText && (
          <div className="text-[11px] text-charcoal-60 mt-1 italic">
            {attributionText}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 self-center ml-1">
        {actionType === "info" ? (
          <span className="flex items-center gap-1 text-[11px] text-charcoal-80">
            <FontAwesomeIcon icon={faCircleInfo} className="text-[10px]" />
            {action.label}
          </span>
        ) : (
          <ShadcnButton
            variant={action.variant}
            size="sm"
            onClick={() => onAction(recipient.userAccountId)}
            disabled={isSaving}
            className={action.className}
          >
            {action.label}
          </ShadcnButton>
        )}
      </div>
    </div>
  );
}
